import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { login } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Package, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function LoginPage() {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!username || !password) {
            toast.error('Please enter username and password');
            return;
        }

        setLoading(true);
        try {
            const data = await login(username, password);
            localStorage.setItem('token', data.token);
            localStorage.setItem('username', data.username);
            toast.success('Login successful');
            navigate('/');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Invalid credentials');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex" data-testid="login-page">
            {/* Left side - Form */}
            <div className="flex-1 flex items-center justify-center p-6 md:p-12">
                <div className="w-full max-w-sm animate-slide-up">
                    <div className="flex items-center gap-3 mb-8">
                        <div className="p-2 bg-zinc-900 dark:bg-zinc-100 rounded-sm">
                            <Package className="h-6 w-6 text-zinc-100 dark:text-zinc-900" strokeWidth={1.5} />
                        </div>
                        <h1 className="text-2xl font-bold tracking-tight">Tracker</h1>
                    </div>

                    <Card className="border border-border rounded-sm shadow-none">
                        <CardHeader className="p-6 pb-4">
                            <CardTitle className="text-xl font-semibold tracking-tight">Sign in</CardTitle>
                            <CardDescription className="text-sm text-muted-foreground">
                                Enter your credentials to access the dashboard
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="p-6 pt-0">
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="username" className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                                        Username
                                    </Label>
                                    <Input
                                        id="username"
                                        type="text"
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                        placeholder="admin"
                                        className="rounded-sm"
                                        data-testid="username-input"
                                        disabled={loading}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                                        Password
                                    </Label>
                                    <Input
                                        id="password"
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="••••••••"
                                        className="rounded-sm"
                                        data-testid="password-input"
                                        disabled={loading}
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    className="w-full rounded-sm font-medium transition-all active:scale-[0.99]"
                                    disabled={loading}
                                    data-testid="login-button"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Signing in...
                                        </>
                                    ) : (
                                        'Sign in'
                                    )}
                                </Button>
                            </form>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Right side - Image */}
            <div className="hidden lg:flex flex-1 relative bg-zinc-100 dark:bg-zinc-900">
                <div className="absolute inset-0 bg-gradient-to-br from-zinc-900/80 to-zinc-900/40 z-10" />
                <img
                    src="https://images.unsplash.com/photo-1653164514614-172c46a30984?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                    alt="Packages"
                    className="absolute inset-0 w-full h-full object-cover grayscale"
                />
                <div className="relative z-20 flex flex-col justify-end p-12">
                    <p className="text-zinc-300 text-lg max-w-md leading-relaxed">
                        Track all your packages in one place. Simple, minimal, functional.
                    </p>
                </div>
            </div>
        </div>
    );
}
